import { useAuth } from "../App";
import { Eye, EyeOff } from "lucide-react";
import { usePreviewMode, setPreviewAsVisitor } from "../hooks/usePreviewMode";

/**
 * PreviewModeToggle — floating bottom-right pill that lets founder /
 * master-admin flip the current page into "view as anonymous visitor" mode.
 *
 * Renders nothing for non-editors so visitors never see admin chrome.
 *
 * Mount this once at the top of every page that hosts <EditableText>
 * (currently PrelaunchLanding + BetaLaunchLanding). The toggle state is
 * shared globally so flipping it from any page applies everywhere within
 * the same browser tab.
 */
const PreviewModeToggle = () => {
  const { user } = useAuth();
  const previewMode = usePreviewMode();

  const canEdit = (() => {
    if (!user) return false;
    const email = (user.email || "").toLowerCase();
    // Founder-only — even master admins don't see the preview toggle.
    return email === "ramneetrdh@gmail.com" || user.admin_role === "founder";
  })();

  if (!canEdit) return null;

  return (
    <button
      type="button"
      onClick={() => setPreviewAsVisitor(!previewMode)}
      className={`fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg text-sm font-medium transition-all backdrop-blur-md ${
        previewMode
          ? "bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
          : "bg-white/95 border border-[#E5DFEC] text-[#6B4E8A] hover:bg-[#FAF7F2]"
      }`}
      style={{ fontFamily: "Outfit, sans-serif" }}
      data-testid="preview-mode-toggle"
      title={previewMode ? "Exit preview — show editing tools" : "Preview as anonymous visitor"}
    >
      {previewMode ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      <span className="hidden sm:inline">
        {previewMode ? "Exit preview" : "Preview as visitor"}
      </span>
    </button>
  );
};

export default PreviewModeToggle;
